import { createApp } from 'vue'
import { createRouter, createWebHistory } from 'vue-router'
import mitt from 'mitt'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import isBetween from 'dayjs/plugin/isBetween'

import App from './App.vue'
import { initSentry } from '@/api/sentry-init'
import store from '@/tarot_box/helper/store'
import utils from '@/tarot_box/helper/utils'
import Constants from '@/tarot_box/pages/box_input/assets/constants'
import Vue3TouchEvents from 'vue3-touch-events'
import VWave from 'v-wave'
import vueDebounce from 'vue-debounce'
import { LoadingPlugin } from 'vue-loading-overlay'
import AppListenerPlugin from '@/common/app_listener_plugin'
import AppCommanderPlugin from '@/common/app_commander_plugin'
import { TarotBoxRoutes } from '@/tarot_box/router'
import { isNewAppVersion } from '@/utils/appVersion'
import { initGrowthBook, gbKey } from '@/utils/growthbook'


// Safe area 변수 업데이트
function updateSafeAreaVariables() {
    const urlParams = new URLSearchParams(window.location.search)
    const insetTop = parseInt(urlParams.get('inset_top') || '0', 10)
    const insetBottom = parseInt(urlParams.get('inset_bottom') || '0', 10)
    document.documentElement.style.setProperty('--safe-area-top', `${insetTop}px`)
    document.documentElement.style.setProperty('--safe-area-bottom', `${insetBottom}px`)
}

(async () => {
    // 1) GrowthBook 초기화 — 앱 실행 전 반드시 먼저
    let gbInstance = null
    try {
        gbInstance = await initGrowthBook()
        console.log('✅ GrowthBook initialized successfully')
    } catch (err) {
        console.error('❌ GrowthBook init failed:', err)
    }

    // 3) Vue 앱 생성
    const app = createApp(App)

    // 4) GrowthBook 인스턴스 제공
    if (gbInstance) {
        app.provide(gbKey, gbInstance)
        app.config.globalProperties.$growthbook = gbInstance
        app.provide('growthbook', gbInstance)
    }

    // 5) $isWeb 글로벌 프로퍼티 설정
    app.config.globalProperties.$isWeb = () => {
        const params = new URLSearchParams(window.location.search)
        return params.get('is_web') === 'true' || params.get('os_type') === 'web'
    }

    // 6) Sentry, router 설정
    const router = createRouter({
        history: createWebHistory(),
        routes: TarotBoxRoutes,
    })
    initSentry(app, router)

    // 7) Event bus
    const eventBus = mitt()
    app.provide('eventBus', eventBus)

    // 8) 전역 디렉티브
    app.directive('focus', {
        mounted(el) {
            el.focus()
        }
    })
    app.directive('web', {
        beforeMount(el) {
            const params = new URLSearchParams(window.location.search)
            if (params.get('is_web') !== 'true' && params.get('os_type') !== 'web') el.style.display = 'none'
        }
    })
    app.directive('app', {
        beforeMount(el) {
            const params = new URLSearchParams(window.location.search)

            // is_share가 true이거나, is_web이 있거나, os_type이 web이면 숨김
            if (params.get('is_share') === 'true' || params.has('is_web') || params.get('os_type') === 'web') {
                el.style.display = 'none'
            }
        }
    })

    app.directive('safe-area-top', {
        mounted(el) {
            if (isNewAppVersion()) {
                const insetTop = parseInt(new URLSearchParams(window.location.search).get('inset_top') || '0', 10)
                if (insetTop > 0) {
                    el.style.paddingTop = `calc(${el.style.paddingTop || '0px'} + ${insetTop}px)`
                }
            }
        }
    })
    app.directive('safe-area-bottom', {
        mounted(el) {
            if (isNewAppVersion()) {
                const insetBottom = parseInt(new URLSearchParams(window.location.search).get('inset_bottom') || '0', 10)
                if (insetBottom > 0) {
                    el.style.paddingBottom = `calc(${el.style.paddingBottom || '0px'} + ${insetBottom}px)`
                }
            }
        }
    })

    // 9) 에러 핸들러
    app.config.errorHandler = (err, vm, info) => {
        if (
            err instanceof TypeError &&
            (err.message.includes("null is not an object (evaluating '_.value.contains')") ||
                err.message.includes("reading 'contains'"))
        ) {
            console.warn('Ignored TypeError from vue-bottom-sheet:', err.message)
            return
        }
        console.error('Unhandled error:', err, info)
    }

    // 10) 플러그인 & 유틸 적용
    app.use(router)
        .use(store)
        .use(utils)
        .use(Constants)
        .use(Vue3TouchEvents, { swipeTolerance: 50 })
        .use(VWave, { color: '#000000', initialOpacity: 0.3, duration: 0.3, easing: 'ease-in' })
        .directive('debounce', vueDebounce({ lock: true, defaultTime: '500ms' }))
        .use(LoadingPlugin)
        .use(AppListenerPlugin)
        .use(AppCommanderPlugin)

    // 11) i18n 설정 - 없앰

    // 12) router 훅
    router.beforeEach((to, from, next) => {
        to.meta.fromPath = from.fullPath
        next()
    })

    // 13) 앱 마운트
    app.mount('#app')
    console.info('🎉 Vue app mounted with GrowthBook')

    // 14) dayjs 플러그인 확장
    dayjs.extend(utc)
    dayjs.extend(timezone)
    dayjs.extend(customParseFormat)
    dayjs.extend(isBetween)

    // 15) Safe area CSS 값 반영
    updateSafeAreaVariables()
})()
